/* Whisply storefront behaviour: countdowns, header, quick add, accordions, reveals, toasts.
   Cart writes go through Shopify's standard cart event so Horizon's drawer re-renders and opens. */
import { CartLinesUpdateEvent } from '@shopify/events';

const qs = (s, c = document) => c.querySelector(s);
const qsa = (s, c = document) => [...c.querySelectorAll(s)];
const pad = (n) => String(n).padStart(2, '0');
const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';

const wScrollY = () => { const w = document.querySelector('.page-wrapper'); return w && w.scrollHeight > w.clientHeight + 5 ? w.scrollTop : window.scrollY; };
window.wScrollY = wScrollY;

/* ---------- countdowns ----------
   data-w-cd="midnight" counts to local midnight; data-w-cd="rolling:45" loops every 45 minutes
   per visitor; data-w-cd="date:2026-10-31T23:59" counts to a fixed moment. */
function cdTarget(el) {
  const mode = el.dataset.wCd || 'week';
  const now = Date.now();
  if (mode.startsWith('rolling:')) {
    const mins = parseFloat(mode.split(':')[1]) || 45, len = mins * 60000, key = 'w_cd_' + mins;
    let start = 0;
    try { start = +localStorage.getItem(key) || 0; if (!start) { start = now; localStorage.setItem(key, start); } } catch (e) { start = now - (now % len); }
    return start + Math.ceil((now - start + 1) / len) * len;
  }
  if (mode.startsWith('date:')) return new Date(mode.slice(5)).getTime();
  const d = new Date();
  if (mode === 'midnight') { d.setHours(24, 0, 0, 0); return d.getTime(); }
  d.setDate(d.getDate() + ((7 - d.getDay()) % 7)); d.setHours(24, 0, 0, 0); return d.getTime();
}
function tickSegs() {
  qsa('[data-w-cdseg]').forEach((el) => {
    const s = Math.max(0, Math.floor((cdTarget({ dataset: { wCd: el.dataset.wCdseg } }) - Date.now()) / 1000));
    const v = { d: Math.floor(s / 86400), h: Math.floor((s % 86400) / 3600), m: Math.floor((s % 3600) / 60), s: s % 60 };
    el.querySelectorAll('[data-u]').forEach((u) => {
      const t = String(u.dataset.u === 'd' || u.dataset.u === 'h' ? v[u.dataset.u] : pad(v[u.dataset.u]));
      if (u.textContent !== t) { u.textContent = t; u.classList.remove('is-tick'); void u.offsetWidth; u.classList.add('is-tick'); }
    });
  });
}
function tick() {
  tickSegs();
  qsa('[data-w-cd]').forEach((el) => {
    const s = Math.max(0, Math.floor((cdTarget(el) - Date.now()) / 1000));
    const D = Math.floor(s / 86400), H = Math.floor((s % 86400) / 3600), M = Math.floor((s % 3600) / 60), S = s % 60;
    el.textContent = D ? D + 'd ' + H + 'h ' + pad(M) + 'm ' + pad(S) + 's' : H ? H + 'h ' + pad(M) + 'm ' + pad(S) + 's' : M + 'm ' + pad(S) + 's';
  });
}
tick(); setInterval(tick, 1000);

/* ---------- toast ---------- */
let toastEl, toastTimer;
export function toast(msg) {
  if (!toastEl) {
    toastEl = document.createElement('div');
    toastEl.className = 'w-toast'; toastEl.setAttribute('role', 'status'); toastEl.setAttribute('aria-live', 'polite');
    toastEl.innerHTML = '<i></i><span></span>';
    document.body.appendChild(toastEl);
  }
  toastEl.querySelector('span').textContent = msg;
  requestAnimationFrame(() => toastEl.classList.add('is-show'));
  clearTimeout(toastTimer); toastTimer = setTimeout(() => toastEl.classList.remove('is-show'), 2600);
}

/* ---------- cart ---------- */
async function refreshCount(count) {
  if (count == null) {
    try { count = (await (await fetch(root + 'cart.js', { headers: { Accept: 'application/json' } })).json()).item_count; } catch (e) { return; }
  }
  qsa('[data-w-cart-count]').forEach((b) => {
    if (b.textContent !== String(count)) { b.textContent = count; b.classList.remove('is-bump'); void b.offsetWidth; b.classList.add('is-bump'); }
  });
}
document.addEventListener('shopify:cart:lines-update', (e) => { (e.promise || Promise.resolve()).then(() => refreshCount()).catch(() => refreshCount()); });

function openDrawer() {
  const c = document.getElementById('w-cart');
  if (c && typeof c.open === 'function') { c.open(); return true; }
  const d = document.getElementById('cart-drawer');
  if (d && typeof d.open === 'function') { d.open(); return true; }
  return false;
}

/* items: [{ id, quantity, properties }] */
export async function addToCart(items, source = document.body) {
  const deferred = CartLinesUpdateEvent.createPromise();
  source.dispatchEvent(new CartLinesUpdateEvent({
    action: 'add', context: 'product',
    lines: items.map((i) => ({ merchandiseId: String(i.id), quantity: i.quantity || 1 })),
    promise: deferred.promise
  }));
  try {
    const res = await fetch(root + 'cart/add.js', {
      method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items })
    });
    const data = await res.json();
    const cart = await (await fetch(root + 'cart.js', { headers: { Accept: 'application/json' } })).json();
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: { items: cart.items, source: 'whisply', itemCount: items.reduce((a, i) => a + (i.quantity || 1), 0), didError: !res.ok }
    });
    if (!res.ok) throw new Error(data.description || data.message || 'Could not add to cart');
    refreshCount(cart.item_count);
    openDrawer();
    return data;
  } catch (err) {
    deferred.resolve({ cart: null, detail: { didError: true, source: 'whisply' } });
    toast(err.message || 'Could not add to cart');
    throw err;
  }
}

/* ---------- header ---------- */
function initHeader(scope = document) {
  qsa('[data-w-head]', scope).forEach((head) => {
    if (head._w) return; head._w = true;
    const btn = qs('[data-w-menu]', head), menu = qs('.w-mnav', head);
    btn && btn.addEventListener('click', () => {
      const open = menu.classList.toggle('is-open');
      btn.setAttribute('aria-expanded', open);
    });
    document.addEventListener('click', (e) => { if (menu && menu.classList.contains('is-open') && !head.contains(e.target)) { menu.classList.remove('is-open'); btn.setAttribute('aria-expanded', 'false'); } });
    qsa('[data-w-open-cart]', head).forEach((b) => b.addEventListener('click', (e) => {
      if (openDrawer()) e.preventDefault();
    }));
    qsa('[data-w-open-search]', head).forEach((b) => b.addEventListener('click', (e) => {
      const modal = document.getElementById('search-modal');
      if (modal && typeof modal.showDialog === 'function') { e.preventDefault(); modal.showDialog(); }
    }));
    const onScroll = () => head.classList.toggle('is-scrolled', wScrollY() > 40);
    document.addEventListener('scroll', onScroll, { passive: true, capture: true }); onScroll();
  });
}

/* ---------- quick add on cards ---------- */
document.addEventListener('click', async (e) => {
  const b = e.target.closest('[data-w-quick-add]');
  if (!b) return;
  e.preventDefault(); e.stopPropagation();
  if (b.dataset.wBusy) return;
  b.dataset.wBusy = '1';
  const label = b.innerHTML;
  try {
    await addToCart([{ id: b.dataset.wQuickAdd, quantity: 1 }], b);
    b.classList.add('is-done'); b.innerHTML = '✓ Added';
    syncPairs();
    toast((b.dataset.wTitle || 'Item') + ' added to cart');
  } catch (err) { /* toast already shown */ }
  setTimeout(() => { b.classList.remove('is-done'); b.innerHTML = label; delete b.dataset.wBusy; }, 1800);
});

/* colour dots on cards swap the photo */
document.addEventListener('pointerover', (e) => {
  const dot = e.target.closest('[data-w-card-color]');
  if (!dot) return;
  const card = dot.closest('.w-card');
  const img = card && card.querySelector('.w-card__main img');
  if (img && dot.dataset.wImg) { img.srcset = ''; img.src = dot.dataset.wImg; }
  const qa = card && card.querySelector('[data-w-quick-add]');
  if (qa && dot.dataset.wVariant) qa.dataset.wQuickAdd = dot.dataset.wVariant;
  if (card) card.querySelectorAll('[data-w-card-color]').forEach((d) => d.classList.toggle('is-on', d === dot));
  card && card.dispatchEvent(new CustomEvent('whisply:card-color', { detail: { color: dot.dataset.wCardColor } }));
});

/* ---------- accordions ---------- */
document.addEventListener('click', (e) => {
  const b = e.target.closest('[data-w-acc] > .w-acc-item > button');
  if (!b) return;
  const item = b.parentElement, group = item.parentElement, open = !item.classList.contains('is-open');
  if (group.dataset.wAcc !== 'multi') qsa(':scope > .w-acc-item', group).forEach((x) => { x.classList.remove('is-open'); x.firstElementChild.setAttribute('aria-expanded', 'false'); });
  item.classList.toggle('is-open', open); b.setAttribute('aria-expanded', open);
});

/* ---------- reveal on scroll ---------- */
const io = 'IntersectionObserver' in window ? new IntersectionObserver((es) => es.forEach((en) => { if (en.isIntersecting) { en.target.classList.add('is-in'); io.unobserve(en.target); } }), { rootMargin: '0px 0px -8% 0px' }) : null;
function initReveal(scope = document) { qsa('.w-reveal:not(.is-in)', scope).forEach((el) => (io ? io.observe(el) : el.classList.add('is-in'))); }

/* ---------- auto-scrolling rails: drift until the shopper interacts ---------- */
function initAutoscroll(scope = document) {
  qsa('[data-w-autoscroll]', scope).forEach((el) => {
    if (el._as) return; el._as = true;
    // clone the tiles once so the rail loops seamlessly
    [...el.children].forEach((c) => { const k = c.cloneNode(true); k.setAttribute('aria-hidden', 'true'); k.querySelectorAll('video').forEach((v) => { v.muted = true; v.play && v.play().catch(() => {}); }); el.appendChild(k); });
    let paused = false, visible = false, raf = 0, acc = 0, resumeT = 0;
    const pause = () => { paused = true; clearTimeout(resumeT); };
    const resume = () => { clearTimeout(resumeT); resumeT = setTimeout(() => { paused = false; }, 2500); };
    el.addEventListener('pointerdown', pause, { passive: true });
    el.addEventListener('touchstart', pause, { passive: true });
    el.addEventListener('pointerup', resume, { passive: true });
    el.addEventListener('touchend', resume, { passive: true });
    el.addEventListener('pointerenter', (e) => { if (e.pointerType === 'mouse') pause(); });
    el.addEventListener('pointerleave', (e) => { if (e.pointerType === 'mouse') resume(); });
    el.addEventListener('wheel', () => { pause(); resume(); }, { passive: true });
    el.style.scrollSnapType = 'none';
    const step = () => {
      if (visible && !paused && el.scrollWidth > el.clientWidth + 4) {
        acc += 0.7;
        if (acc >= 1) { el.scrollLeft += Math.floor(acc); acc -= Math.floor(acc); }
        const half = el.scrollWidth / 2;
        if (el.scrollLeft >= half) el.scrollLeft -= half;
      }
      raf = requestAnimationFrame(step);
    };
    new IntersectionObserver(([en]) => { visible = en.isIntersecting; }).observe(el);
    raf = requestAnimationFrame(step);
  });
}

/* ---------- scroll animations ---------- */
const AUTO_REVEAL = [
  ['.w-card', 'w-anim-card'], ['.w-pdp__gallery > div', 'w-anim-zoom'], ['.w-rev', 'w-anim-up'],
  ['.w-ugc__tile', 'w-anim-up'], ['.w-faq__item', 'w-anim-up'], ['.w-sec-head', 'w-anim-up'],
  ['.w-trust > span', 'w-anim-up'], ['.w-benefits > span', 'w-anim-up'], ['.w-revs__sum', 'w-anim-up'],
  ['.w-promo', 'w-anim-zoom'], ['.w-tools', 'w-anim-up'], ['.w-deal', 'w-anim-up'], ['.w-gift', 'w-anim-up'],
  ['.w-acc', 'w-anim-up'], ['.w-spot', 'w-anim-up'], ['.w-film', 'w-anim-zoom'], ['.w-bento > div', 'w-anim-card']
];
const animIO = 'IntersectionObserver' in window ? new IntersectionObserver((es) => es.forEach((en) => {
  if (en.isIntersecting) { en.target.classList.add('is-in'); animIO.unobserve(en.target); }
}), { rootMargin: '0px 0px -6% 0px', threshold: 0.08 }) : null;
function initAnims(scope = document) {
  return;
  if (!animIO || matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  AUTO_REVEAL.forEach(([sel, cls]) => {
    qsa(sel, scope).forEach((el) => {
      if (el.dataset.wAnim) return;
      el.dataset.wAnim = '1';
      const sibs = [...el.parentElement.children].filter((c) => c.matches(sel));
      const i = sibs.indexOf(el);
      el.style.setProperty('--w-d', ((i % 4) * 90) + 'ms');
      if (el.getBoundingClientRect().top < innerHeight * 0.9 && el.getBoundingClientRect().bottom > 0) return; // already on screen at load
      el.classList.add('w-anim', cls);
      animIO.observe(el);
    });
  });
}
/* parallax: collection hero title + page headings drift on scroll */
let pxEls = [];
function initParallax() { pxEls = qsa('[data-w-parallax]'); }
let pxTick = false;
document.addEventListener('scroll', () => {
  if (pxTick || !pxEls.length) return; pxTick = true;
  requestAnimationFrame(() => {
    const y = wScrollY();
    pxEls.forEach((el) => { if (y < innerHeight * 1.2) el.style.transform = 'translate3d(0,' + (y * 0.18).toFixed(1) + 'px,0)'; });
    pxTick = false;
  });
}, { passive: true, capture: true });

/* ---------- offers popup for "complete your 3-for-2" rows ---------- */
let offerCtx = 0;
document.addEventListener('click', (e) => { if (e.target.closest('[data-w-offer-pop] [data-w-qb], [data-w-offer-pop] [data-w-quick-add]')) offerCtx = Date.now(); }, true);
function offerPopup(pairs) {
  const old = document.querySelector('.w-offer'); if (old) old.remove();
  const p = document.createElement('div'); p.className = 'w-offer'; p.setAttribute('role', 'dialog'); p.setAttribute('aria-label', 'Your deals');
  const row = (on, badge, t, s, need) => '<li class="' + (on ? 'is-on' : '') + '"><i>' + badge + '</i><span><b>' + t + '</b><small>' + s + '</small></span><em>' + (on ? '✓ Unlocked' : 'Add ' + need + ' more') + '</em></li>';
  p.innerHTML = '<button type="button" class="w-offer__x" aria-label="Close">×</button>' +
    '<span class="w-offer__eyebrow">Nice pick! 2 ways to save</span>' +
    '<b class="w-offer__title">Mix &amp; match any frames</b>' +
    '<ul>' + row(pairs >= 2, '50%', 'Buy 1, get 1 50% off', 'Your 2nd pair is half price', Math.max(1, 2 - pairs)) + row(pairs >= 3, 'FREE', 'Buy 2, get 1 free', 'Your 3rd pair is on us', Math.max(1, 3 - pairs)) + '</ul>' +
    '<div class="w-offer__bar"><div style="width:' + Math.min(100, (pairs / 3) * 100) + '%"></div></div>' +
    '<small class="w-offer__note">' + (pairs >= 3 ? 'Both deals unlocked — applied automatically at checkout.' : pairs === 2 ? 'Add 1 more pair and the 3rd is FREE.' : 'Add 1 more pair for 50% off it — 2 more and one is FREE.') + '</small>' +
    '<div class="w-offer__ctas"><button type="button" class="w-offer__keep">Keep shopping</button><button type="button" class="w-offer__cart">View cart</button></div>';
  document.body.appendChild(p);
  const hide = () => { p.classList.remove('is-show'); setTimeout(() => p.remove(), 500); };
  p.querySelector('.w-offer__x').onclick = hide; p.querySelector('.w-offer__keep').onclick = hide;
  p.querySelector('.w-offer__cart').onclick = () => { hide(); openDrawer(); };
  requestAnimationFrame(() => requestAnimationFrame(() => p.classList.add('is-show')));
  setTimeout(hide, 9000);
}

/* ---------- multi-pair deal: cards reveal the next-pair price ---------- */
const EYEWEAR = ['glasses', 'bl'];
let pairsKnown = null;
let pillEl = null, pillT = 0;
function dealPill(badge, title, sub) {
  if (pillEl) { pillEl.remove(); clearTimeout(pillT); }
  const t = pillEl = document.createElement('div'); t.className = 'w-unlock'; t.setAttribute('role', 'status');
  t.innerHTML = '<i>' + badge + '</i><b>' + title + '</b><small>' + sub + '</small><button type="button" aria-label="Dismiss">×</button>';
  document.body.appendChild(t);
  const hide = () => { t.classList.remove('is-show'); setTimeout(() => t.remove(), 800); if (pillEl === t) pillEl = null; };
  t.querySelector('button').addEventListener('click', hide);
  requestAnimationFrame(() => requestAnimationFrame(() => t.classList.add('is-show')));
  pillT = setTimeout(hide, 8000);
}
const PILLS = { 1: ['50%', 'Your 2nd pair is 50% off', 'Mix & match any frame'], 2: ['FREE', '3rd pair unlocked — it’s FREE', 'Add one more frame'] };
function setPairs(n) {
  const level = n >= 2 ? 2 : n >= 1 ? 1 : 0;
  const prev = pairsKnown; pairsKnown = level;
  document.documentElement.dataset.wPairs = String(level);
  let seen = ''; try { seen = sessionStorage.getItem('w_deal_seen') || ''; } catch (e) {}
  return; // deal pop-ups disabled at the merchant's request
  const show = (prev !== null && level > prev) || (prev === null && level > 0 && !seen.includes(String(level)));
  if (show && PILLS[level]) { dealPill(...PILLS[level]); try { sessionStorage.setItem('w_deal_seen', seen + level); } catch (e) {} }
}
async function syncPairs() {
  try {
    const c = await (await fetch(root + 'cart.js', { headers: { Accept: 'application/json' } })).json();
    const n = c.items.filter((i) => EYEWEAR.includes(String(i.product_type || '').toLowerCase()) && i.final_price > 0 && !(i.properties && i.properties['_Lab Cost'])).reduce((a, i) => a + i.quantity, 0);
    setPairs(n);
  } catch (e) {}
}
document.addEventListener('shopify:cart:lines-update', (e) => { (e.promise || Promise.resolve()).then(syncPairs, syncPairs); });
document.addEventListener('whisply:cart', syncPairs);
syncPairs();

function init(scope) {
  [initHeader, initReveal, initAutoscroll, initAnims].forEach((fn) => { try { fn(scope); } catch (e) { console.warn('[whisply]', fn.name, e); } });
  try { initParallax(); tick(); } catch (e) {}
}
document.addEventListener('whisply:cards', () => initAnims(document));
window.Whisply = Object.assign(window.Whisply || {}, { addToCart, toast, refreshCount, openCart: openDrawer });
init(document);
document.addEventListener('shopify:section:load', (e) => init(e.target));

window.Whisply = Object.assign(window.Whisply || {}, { addToCart, toast, refreshCount, openCart: openDrawer });

/* ---------- scroll-linked 3D depth (disabled: content shows immediately) ---------- */
(() => {
  return;
  if (matchMedia('(prefers-reduced-motion: reduce)').matches || (window.Shopify && Shopify.designMode)) return;
  const GROUPS = [
    { sel: '.w-film, .w-promo, .w-spot', fx: (p, el) => `perspective(1400px) rotateX(${(1 - p) * 22}deg) scale(${0.9 + p * 0.1})`, origin: '50% 100%' },
    { sel: '.w-bento > div, .w-pg__tile, .w-ugc__tile', fx: (p, el, i) => `perspective(1000px) rotateY(${(1 - p) * (i % 2 ? -18 : 18)}deg) translateZ(${(p - 1) * 60}px)`, origin: '50% 50%' },
    { sel: '.w-grid > .w-card', fx: (p, el, i) => `perspective(1000px) rotateX(${(1 - p) * 16}deg) rotateY(${(1 - p) * (i % 2 ? -8 : 8)}deg)`, origin: '50% 100%' },
    { sel: '.w-sec-head h2, .w-revs__sum h2, .w-faq h2', fx: (p) => `perspective(800px) rotateX(${(1 - p) * 35}deg) translateY(${(1 - p) * 20}px)`, origin: '50% 100%' }
  ];
  let items = [];
  const collect = () => {
    items = [];
    GROUPS.forEach((g) => document.querySelectorAll(g.sel).forEach((el, i) => {
      el.style.transformOrigin = g.origin; el.style.willChange = 'transform';
      el.classList.remove('w-anim', 'w-anim-up', 'w-anim-card', 'w-anim-zoom');
      items.push({ el, g, i });
    }));
  };
  let ticking = false;
  const update = () => {
    ticking = false;
    const vh = innerHeight;
    for (const it of items) {
      const r = it.el.getBoundingClientRect();
      if (r.bottom < -50 || r.top > vh + 50) continue;
      const p = Math.max(0, Math.min(1, (vh - r.top) / (vh * 0.55)));
      it.el.style.transform = p >= 1 ? '' : it.g.fx(p, it.el, it.i);
      it.el.style.opacity = String(Math.min(1, 0.35 + p * 0.9));
    }
  };
  const onScroll = () => { if (!ticking) { ticking = true; requestAnimationFrame(update); } };
  const start = () => { collect(); update(); };
  document.addEventListener('scroll', onScroll, { passive: true, capture: true });
  addEventListener('resize', onScroll);
  document.addEventListener('whisply:cards', () => setTimeout(start, 50));
  if (document.readyState === 'complete') start(); else addEventListener('load', start);
  setTimeout(start, 1200);
})();

/* ---------- phones: show the on-face photo while a card row is in the middle of the screen ---------- */
(() => {
  if (!matchMedia('(hover: none)').matches) return;
  let tick = false;
  const run = () => {
    tick = false;
    const h = innerHeight, top = h * 0.4, bot = h * 0.6;
    document.querySelectorAll('.w-card').forEach((c) => {
      if (!c.querySelector('.w-card__alt')) return;
      const r = c.getBoundingClientRect(), mid = r.top + r.height / 2;
      c.classList.toggle('is-alt', mid >= top && mid <= bot);
    });
  };
  const on = () => { if (!tick) { tick = true; requestAnimationFrame(run); } };
  document.addEventListener('scroll', on, { passive: true, capture: true });
  addEventListener('resize', on);
  document.addEventListener('whisply:cards', on);
  setTimeout(run, 600);
})();

/* ---------- videos load only near the screen and pause when off it ---------- */
(() => {
  if (!('IntersectionObserver' in window)) { document.querySelectorAll('video[data-w-lazyvid]').forEach((v) => { v.src = v.dataset.src; }); return; }
  const io = new IntersectionObserver((es) => es.forEach((e) => {
    const v = e.target;
    if (e.isIntersecting) { if (!v.src && v.dataset.src) { v.src = v.dataset.src; } const p = v.play && v.play(); p && p.catch && p.catch(() => {}); }
    else if (v.src && !v.paused) v.pause();
  }), { rootMargin: '300px 0px' });
  const scan = (root) => (root || document).querySelectorAll('video[data-w-lazyvid], .w-ugc video, .w-ring video, .w-col video, .w-film video, .w-promo video').forEach((v) => { if (!v._wio) { v._wio = 1; io.observe(v); } });
  scan(); document.addEventListener('shopify:section:load', (e) => scan(e.target)); setTimeout(scan, 1500);
})();

/* ---------- lightbox for [data-w-zoom] photos ---------- */
(() => {
  let box, img, list = [], idx = 0, lastFocus = null, sx = null;
  const build = () => {
    box = document.createElement('div'); box.className = 'w-lb'; box.hidden = true; box.setAttribute('role', 'dialog'); box.setAttribute('aria-modal', 'true');
    box.innerHTML = '<button type="button" class="w-lb__x" aria-label="Close">×</button><button type="button" class="w-lb__nav w-lb__nav--p" aria-label="Previous">←</button><img alt=""><button type="button" class="w-lb__nav w-lb__nav--n" aria-label="Next">→</button><span class="w-lb__count"></span>';
    document.body.appendChild(box); img = box.querySelector('img');
    box.addEventListener('click', (e) => { if (e.target.closest('.w-lb__nav--p')) return go(-1); if (e.target.closest('.w-lb__nav--n')) return go(1); if (e.target === img) return; close(); });
    box.addEventListener('touchstart', (e) => { sx = e.touches[0].clientX; }, { passive: true });
    box.addEventListener('touchend', (e) => { if (sx === null) return; const dx = e.changedTouches[0].clientX - sx; sx = null; if (Math.abs(dx) > 40) go(dx < 0 ? 1 : -1); });
  };
  const show = () => { img.src = list[idx].dataset.wZoom; box.querySelector('.w-lb__count').textContent = (idx + 1) + ' / ' + list.length; box.classList.toggle('is-single', list.length < 2); };
  const go = (d) => { idx = (idx + d + list.length) % list.length; show(); };
  const open = (el) => {
    if (!box) build();
    const scope = el.closest('section') || document; list = [...scope.querySelectorAll('[data-w-zoom]')]; idx = Math.max(0, list.indexOf(el));
    lastFocus = el; show(); box.hidden = false; requestAnimationFrame(() => box.classList.add('is-open'));
    document.documentElement.classList.add('w-cd-lock'); box.querySelector('.w-lb__x').focus({ preventScroll: true });
  };
  const close = () => { box.classList.remove('is-open'); document.documentElement.classList.remove('w-cd-lock'); setTimeout(() => { box.hidden = true; }, 250); lastFocus && lastFocus.focus({ preventScroll: true }); };
  document.addEventListener('click', (e) => { const t = e.target.closest('[data-w-zoom]'); if (t) { e.preventDefault(); open(t); } });
  document.addEventListener('keydown', (e) => {
    const t = e.target.closest && e.target.closest('[data-w-zoom]'); if (t && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); open(t); return; }
    if (!box || box.hidden) return; if (e.key === 'Escape') close(); if (e.key === 'ArrowRight') go(1); if (e.key === 'ArrowLeft') go(-1);
  });
})();
